import { createSelector } from 'reselect'

import { checkPerm } from 'utils/permManager'
import {
  atLeastCampaignsOnlyPlan,
  atLeastIronscalesProtectPlan,
  atLeastStarterPlan,
} from 'utils/plans'
import { URL_CONST } from 'utils/urlConsts'

import { PLAN_TYPES } from 'containers/App/constants'
import {
  selectActualLicenses,
  selectIsStaticSalesDemo,
  selectPartner,
  selectRegion,
  selectSelfFeatureFlags,
  selectSelfLicenses,
  selectSelfPermissions,
} from 'containers/App/selectors'

const selectUserDropdownItems = (state) => {
  const selfPermissions = selectSelfPermissions(state)
  const actualLicenses = selectActualLicenses(state)
  const isStaticSalesDemo = selectIsStaticSalesDemo(state)
  const items = [
    {
      text: 'Resources',
      url: URL_CONST.resources.self,
      type: 'internal',
      id: 'resources_button',
      blocked: isStaticSalesDemo,
      className: 'gt-p',
    },
    {
      text: 'Log Out',
      url: URL_CONST.login.signout,
      type: 'external',
      id: 'logout_button',
      className: 'red',
    },
  ]

  if (
    selfPermissions.admin &&
    actualLicenses?.plan &&
    actualLicenses?.plan !== PLAN_TYPES.STARTER
  ) {
    items.unshift({
      text: 'Activities',
      url: URL_CONST.activities.self,
      type: 'internal',
      id: 'activities_button',
      className: 'gt-p',
    })
  }
  return items
}

const selectIncidentsVisibility = createSelector(
  selectSelfPermissions,
  selectSelfLicenses,
  (selfPermissions, selfLicenses) =>
    checkPerm(selfPermissions, [
      'admin',
      'irontraps_action',
      'manage_irontraps',
      'irontraps_viewer',
    ]) && atLeastStarterPlan(selfLicenses.plan)
)

const selectInvestigationVisibility = createSelector(
  selectSelfLicenses,
  selectSelfFeatureFlags,
  selectSelfPermissions,
  (selfLicenses, ff, selfPermissions) =>
    atLeastIronscalesProtectPlan(selfLicenses.plan) &&
    !ff.DISABLE_INVESTIGATION_TAB &&
    checkPerm(selfPermissions, [
      'admin',
      'irontraps_action',
      'manage_irontraps',
      'irontraps_viewer',
    ])
)

const selectAwarenessVisibility = createSelector(
  selectSelfPermissions,
  selectSelfLicenses,
  (selfPermissions, selfLicenses) =>
    checkPerm(selfPermissions, ['admin', 'edit_campaigns']) &&
    atLeastCampaignsOnlyPlan(selfLicenses.plan)
)

const selectMailboxesVisibility = createSelector(selectSelfPermissions, (selfPermissions) =>
  checkPerm(selfPermissions, ['admin'])
)

const selectGroupSettingsVisibility = createSelector(
  selectSelfPermissions,
  selectPartner,
  selectIsStaticSalesDemo,
  (selfPermissions, partner, isStaticSalesDemo) =>
    checkPerm(selfPermissions, ['admin']) && partner && !isStaticSalesDemo
)

const selectAccountSetupVisibility = createSelector(selectSelfPermissions, (selfPermissions) =>
  checkPerm(selfPermissions, ['admin'])
)

const selectDmarcVisibility = createSelector(
  selectRegion,
  selectSelfPermissions,
  (region, selfPermissions) => (region === 'US' || region === 'EU') && checkPerm(selfPermissions, ['admin'])
)

export {
  selectUserDropdownItems,
  selectIncidentsVisibility,
  selectInvestigationVisibility,
  selectAwarenessVisibility,
  selectMailboxesVisibility,
  selectGroupSettingsVisibility,
  selectAccountSetupVisibility,
  selectDmarcVisibility,
}
